// pwa/src/components/LocationModalContent.js
import React, { useState, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import DynamicSelect from "./DynamicSelect";
import DynamicProductSearchAutocomplete from "./DynamicProductSearchAutocomplete";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Divider from "@mui/material/Divider";

const ProductRow = ({
  product = {},
  initialQuantity = 1,
  onQuantityChange,
  onDelete,
}) => {
  const [quantity, setQuantity] = useState(initialQuantity);

  useEffect(() => {
    handleQuantityChange(quantity);
  }, [quantity]);

  const handleIncrease = () => {
    setQuantity((prevQuantity) => prevQuantity + 1);
  };

  const handleDecrease = () => {
    setQuantity((prevQuantity) => (prevQuantity > 1 ? prevQuantity - 1 : 1));
  };

  const handleQuantityChange = (newQuantity) => {
    onQuantityChange(product.id, newQuantity);
  };

  const handleDelete = () => {
    onDelete(product.id);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          display: "flex", // Usamos flexbox para alinear los IconButton y Typography
          alignItems: "center",
          border: "1px solid #ccc", // Borde para el grupo de cantidad
          borderRadius: "4px",
          minWidth: "150px", // Ajusta según el tamaño deseado para el grupo
          justifyContent: "space-between", // Para distribuir los elementos dentro del Box
          p: "0.5em", // Padding interno
        }}
      >
        {/* Botón para restar cantidad */}
        <IconButton
          aria-label="restar cantidad"
          onClick={handleDecrease}
          disabled={quantity === 1}
          size="small"
        >
          <RemoveIcon />
        </IconButton>

        {/* Display de cantidad */}
        <Typography
          variant="body1"
          sx={{ minWidth: "24px", textAlign: "center" }}
        >
          {quantity}
        </Typography>

        {/* Botón para sumar cantidad */}
        <IconButton
          aria-label="sumar cantidad"
          onClick={handleIncrease}
          size="small"
        >
          <AddIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, textAlign: "left" }}>
        <Typography variant="body1" noWrap>
          {product.name}
        </Typography>
      </Box>

      {/* Botón para eliminar */}
      <IconButton
        aria-label="eliminar producto"
        onClick={handleDelete}
        color="error"
        size="small"
      >
        <DeleteIcon />
      </IconButton>
    </Stack>
  );
};

const LocationModalContent = React.forwardRef(
  ({ shopId, orderId, onSaveSuccess }, ref) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const textFieldRef = useRef(null);
    const [hashId, setHashId] = useState("");
    const [locations, setLocations] = useState([]);
    const [orderStatuses, setOrderStatuses] = useState([]);
    const [quantityAdd, setQuantityAdd] = useState(1);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [resetKey, setResetKey] = useState(false);
    const [formData, setFormData] = useState({
      shopId: shopId,
      name: "",
      location: "",
      order_status: {},
      products: [],
      description: "",
      active: false,
    });

    const qrLink = process.env.REACT_APP_FRONT_URL + "/qr/" + hashId;

    React.useImperativeHandle(ref, () => ({
      handleClickOpen,
      handleClose,
    }));

    // Console log para debugging de shopId (si se necesita)
    useEffect(() => {
      console.log("shopId in ModalContent:", shopId);
      console.log("formData:", formData);
      console.log("locations:", locations);
      console.log("orderStatuses:", orderStatuses);
      console.log("selectedProduct:", selectedProduct);
      console.log("quantityAdd:", quantityAdd);
      console.log("resetKey:", resetKey);
    }, [shopId, formData, locations, orderStatuses, selectedProduct, quantityAdd, resetKey]);

    useEffect(() => {
      // Asegúrate de resetear shopId en formData cuando shopId cambie
      setFormData((prev) => ({ ...prev, shopId: shopId }));

      if (orderId) {
        // Modo edición: Cargar datos del producto
        console.log(`Fetching location data for ID: ${orderId}`);
        axios
          .get(process.env.REACT_APP_API_URL + `/api/orders/${orderId}`)
          .then((response) => {
            const orderData = response.data; // Renombrado de productData a orderData para claridad
            setHashId(orderData.hashId || "");
            setFormData({
              shopId: shopId, // Asegura que shopId se mantenga
              createdAt: orderData.createdAt || "",
              orderNumber: orderData.orderNumber || "",
              order_status: orderData.order_status || {},
              status: orderData.status || "",
              location: orderData.location || "",
              products: orderData.products || [],
              description: orderData.description || "",
              // CORREGIDO: Convertir 'active' a booleano al cargar desde la API
              active: !!orderData.active, // Converts 0/1 or null/undefined to false/true
            });
            console.log("orderData data loaded:", orderData);
            console.log("orderData.location.id:", orderData.location.id);
          })
          .catch((error) => {
            console.error(
              `Error fetching location data for ID ${orderId}:`,
              error
            );
            // Opcional: mostrar un mensaje de error al usuario
            // Si hay un error al cargar, reseteamos el formulario como si fuera creación
            setFormData({
              shopId: shopId,
              name: "",
              description: "",
              createdAt: "",
              orderNumber: "",
              order_status: {},
              status: "",
              location: "",
              products: [],
              description: "",
              active: false, // CORREGIDO: Resetear a false
            });
          });
      } else {
        // Modo creación: Reiniciar el formulario
        console.log("Modal opened for new location creation. Resetting form.");
        setFormData({
          shopId: shopId,
          name: "",
          description: "",
          createdAt: "",
          orderNumber: "",
          order_status: {},
          status: "",
          location: "",
          products: [],
          description: "",
          active: false, // CORREGIDO: Resetear a false
        });
      }
    }, [orderId, shopId]); // Ejecutar este efecto cada vez que orderId o shopId cambien

    const handleClickOpen = () => {
      setOpen(true);
      // Ya no reiniciamos formData aquí. El useEffect lo hará basado en `orderId`.
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleChange = (event) => {
      const { name, value, type, checked } = event.target; // Destructurar 'type' y 'checked'
      setFormData((prevFormData) => ({
        ...prevFormData,
        // CORREGIDO: Manejar checkboxes correctamente usando 'checked'
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleSubmit = (event) => {
      event.preventDefault();

      // CORREGIDO: Convertir el booleano 'active' a 1 o 0 para la base de datos
      const dataToSend = {
        ...formData,
        active: formData.active ? 1 : 0,
      };

      console.log("Submitting form data:", dataToSend);

      const apiEndpoint = orderId
        ? process.env.REACT_APP_API_URL + `/api/orders/${orderId}` // Para actualización
        : process.env.REACT_APP_API_URL + "/api/orders"; // Para creación

      const httpMethod = orderId ? axios.put : axios.post; // Usar PUT para actualizar, POST para crear

      httpMethod(apiEndpoint, dataToSend) // Usar dataToSend
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            // 200 para PUT/OK, 201 para POST/Created
            console.log("Location operation successful:", response.data);
            handleClose();
            // AÑADIDO: Llamar a la función de callback si se proporciona
            if (onSaveSuccess) {
              onSaveSuccess();
            }
          } else {
            console.log("API response status not successful:", response.status);
          }
        })
        .catch((error) => {
          console.log("handleSubmit location error: ");
          console.error(error);
        });
    };

    const handleLocationChange = (newValue) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        location: locations.find((loc) => loc.id === newValue) || newValue,
      }));
      console.log("Nuevo location seleccionado:", newValue);
    };

    const handleOrderStatusChange = (newValue) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        order_status:
          orderStatuses.find((status) => status.id === newValue) || newValue,
      }));
      console.log("Nuevo order status seleccionado:", newValue);
    };

    const handleProductQuantityChange = (productId, newQuantity) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        products: prevFormData.products.map((p) =>
          p.id === productId
            ? {
                ...p,
                order_products: { ...p.order_products, quantity: newQuantity },
              }
            : p
        ),
      }));
    };

    const removeProduct = (productId) => {
      setFormData((prevFormData) => ({
        ...prevFormData,
        products: prevFormData.products.filter((p) => p.id !== productId),
      }));
    };

    const handleIncreaseAddProduct = () => {
      setQuantityAdd((prevQuantity) => prevQuantity + 1);
    };

    const handleDecreaseAddProduct = () => {
      setQuantityAdd((prevQuantity) =>
        prevQuantity > 1 ? prevQuantity - 1 : 1
      );
    };

    const handleAddProduct = () => {
      if (!selectedProduct) return;
      setFormData((prevFormData) => {
        if (prevFormData.products.some((p) => p.id === selectedProduct.id)) {
          return prevFormData;
        }
        const unitPrice = Number(selectedProduct.price);
        const subtotal = unitPrice * quantityAdd;
        return {
          ...prevFormData,
          products: [
            ...prevFormData.products,
            {
              ...selectedProduct,
              order_products: {
                ...(selectedProduct.order_products || {}),
                quantity: quantityAdd,
                unitPrice,
                subtotal,
              },
            },
          ],
        };
      });
      setSelectedProduct(null);
      setQuantityAdd(1);
      setResetKey((prev) => prev + 1);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        TransitionProps={{
          onEntered: () => {
            if (textFieldRef.current) {
              textFieldRef.current.focus();
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {/* Título dinámico: "Edit Product" o "Create Product" */}
            <FormattedMessage
              id={
                orderId
                  ? "app.header.order.label"
                  : "app.header.order.label"
              }
            />
          </span>
          <Box>
            <Tooltip title="Close">
              <IconButton aria-label="Close" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form style={{ marginTop: "10px" }} onSubmit={handleSubmit}>
            <Grid
              container
              direction={"column"}
              spacing={2}
              className={"settingsContainer"}
            >
              <Grid item>
                <TextField
                  name="orderNumber"
                  id="outlined-basic-title"
                  label="Titulo"
                  fullWidth
                  value={formData.orderNumber}
                  disabled
                  inputRef={textFieldRef}
                />
              </Grid>
              <Grid item>
                <TextField
                  name="createdAt"
                  id="outlined-basic-createdAt"
                  label="Fecha de Creación"
                  fullWidth
                  disabled
                  value={formData.createdAt}
                />
              </Grid>
              <Grid item>
                <DynamicSelect
                  apiModel="locations"
                  componentLabel="Ubicación"
                  onItemChange={handleLocationChange}
                  defaultValue={formData.location.id}
                  onDataLoaded={setLocations}
                />
              </Grid>
              <Grid item>
                <DynamicSelect
                  apiModel="util/order_statuses"
                  componentLabel="Estado del Pedido"
                  onItemChange={handleOrderStatusChange}
                  defaultValue={formData.order_status.id}
                  onDataLoaded={setOrderStatuses}
                />
              </Grid>
              {formData.products.map((product) => (
                <Grid item key={product.id}>
                  <ProductRow
                    product={product}
                    initialQuantity={product.order_products.quantity}
                    onQuantityChange={handleProductQuantityChange}
                    onDelete={removeProduct}
                  />
                </Grid>
              ))}
              <Grid item>
                <Divider sx={{ marginTop: "1em", marginBottom: "1em" }} />

                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      display: "flex", // Usamos flexbox para alinear los IconButton y Typography
                      alignItems: "center",
                      border: "1px solid #ccc", // Borde para el grupo de cantidad
                      borderRadius: "4px",
                      minWidth: "150px", // Ajusta según el tamaño deseado para el grupo
                      justifyContent: "space-between", // Para distribuir los elementos dentro del Box
                      p: "0.5em", // Padding interno
                    }}
                  >
                    {/* Botón para restar cantidad */}
                    <IconButton
                      onClick={handleDecreaseAddProduct}
                      aria-label="restar cantidad"
                      disabled={quantityAdd === 1}
                      size="small"
                    >
                      <RemoveIcon />
                    </IconButton>

                    {/* Display de cantidad */}
                    <Typography
                      variant="body1"
                      sx={{ minWidth: "24px", textAlign: "center" }}
                    >
                      {quantityAdd}
                    </Typography>

                    {/* Botón para sumar cantidad */}
                    <IconButton
                      onClick={handleIncreaseAddProduct}
                      aria-label="sumar cantidad"
                      size="small"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ flex: 1, textAlign: "left" }}>
                    <DynamicProductSearchAutocomplete onProductSelect={setSelectedProduct} resetKey={resetKey} />
                  </Box>

                  {/* Botón para eliminar */}
                  <IconButton
                    onClick={handleAddProduct}
                    aria-label="eliminar producto"
                    color="success"
                    size="small"
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} autoFocus>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default LocationModalContent;
